<template>
  <div
    ref="resizableDiv"
    :class="[ns.b()]"
    :style="{ cursor: disabled ? 'not-allowed' : 'se-resize' }"
    @mousedown.prevent="startResize"
  >
    <slot />
    <div ref="resizer" class="resizer" @mousedown.prevent="startResize" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import { resizeObserverProps } from './resizeObserver'
import { throttle } from './performance'

defineOptions({
  name: 'LtResizeObserver',
})
const ns = useNamespace('resize-observer')
const props = defineProps(resizeObserverProps)
const emit = defineEmits(['update:width', 'update:height', 'onResize'])

const resizableDiv = ref<HTMLElement | null>(null)
const resizer = ref<HTMLElement | null>(null)
let startX = 0
let startY = 0
let startWidth = 0
let startHeight = 0
let rafId: number | null = null
const localWidth = ref<number>(props.width)
const localHeight = ref<number>(props.height)

const resize = (e: MouseEvent) => {
  if (!resizableDiv.value) return

  const newWidth = startWidth + (e.clientX - startX)
  const newHeight = startHeight + (e.clientY - startY)

  if (rafId !== null) {
    cancelAnimationFrame(rafId)
  }

  rafId = requestAnimationFrame(() => {
    resizableDiv.value!.style.width = `${newWidth}px`
    resizableDiv.value!.style.height = `${newHeight}px`

    localWidth.value = newWidth
    localHeight.value = newHeight
    const contentRect = resizableDiv.value!.getBoundingClientRect()
    const entry: ResizeObserverEntry = {
      target: resizableDiv.value!,
      contentRect,
      contentBoxSize: [
        { inlineSize: contentRect.width, blockSize: contentRect.height },
      ],
      borderBoxSize: [
        { inlineSize: contentRect.width, blockSize: contentRect.height },
      ],
      devicePixelContentBoxSize: [
        { inlineSize: contentRect.width, blockSize: contentRect.height },
      ],
    }
    emit('update:width', newWidth)
    emit('update:height', newHeight)
    emit('onResize', entry)
  })
}

let throttledResize: (...args: any[]) => void

if (props.throttle) {
  const interval = typeof props.throttle === 'number' ? props.throttle : 16
  throttledResize = throttle(resize, interval)
} else {
  throttledResize = resize
}

const startResize = (e: MouseEvent) => {
  if (props.disabled || !resizableDiv.value) return
  startX = e.clientX
  startY = e.clientY
  startWidth = resizableDiv.value.offsetWidth
  startHeight = resizableDiv.value.offsetHeight

  document.addEventListener('mousemove', throttledResize)
  document.addEventListener('mouseup', stopResize)
}

const stopResize = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  document.removeEventListener('mousemove', throttledResize)
  document.removeEventListener('mouseup', stopResize)
}

onMounted(() => {
  if (resizableDiv.value) {
    resizableDiv.value.style.width = `${localWidth.value}px` // 初始宽度
    resizableDiv.value.style.height = `${localHeight.value}px` // 初始高度
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', throttledResize)
  document.removeEventListener('mouseup', stopResize)
})

watch([() => props.width, () => props.height], ([newWidth, newHeight]) => {
  if (resizableDiv.value) {
    if (newWidth !== undefined) {
      localWidth.value = newWidth
      resizableDiv.value.style.width = `${newWidth}px`
    }
    if (newHeight !== undefined) {
      localHeight.value = newHeight
      resizableDiv.value.style.height = `${newHeight}px`
    }
  }
})
</script>
